import React, { useEffect, useState, useMemo } from "react";
import { tourBookingApi } from "../../../../../api/tourBookingApi";
import moment from "moment";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const ITEMS_PER_PAGE = 100;

const TourBookingList = () => {
    const [bookings, setBookings] = useState([]);
    const [searchMobile, setSearchMobile] = useState("");
    const [filterMonth, setFilterMonth] = useState("");
    const [appliedMobile, setAppliedMobile] = useState("");
    const [appliedMonth, setAppliedMonth] = useState("");

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const res = await tourBookingApi.getAllBookings();
            setBookings(res.data);
        } catch (err) {
            console.error("Error fetching tour bookings:", err);
        } finally {
            setLoading(false);
        }
    };

    /**  Filter Logic (fires only after Filter button) */
    const filteredData = useMemo(() => {
        return bookings.filter((item) => {
            const matchesMobile = appliedMobile
                ? item.MobileNo?.toString().includes(appliedMobile)
                : true;

            const matchesMonth = appliedMonth
                ? moment(item.BookingDate).format("YYYY-MM") === appliedMonth
                : true;

            return matchesMobile && matchesMonth;
        });
    }, [bookings, appliedMobile, appliedMonth]);

    /**  Pagination */
    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    const paginatedData = filteredData.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    /**  Apply Filter */
    const handleApplyFilter = () => {
        setAppliedMobile(searchMobile.trim());
        setAppliedMonth(filterMonth);
        setPage(1);
    };

    /**  Reset Filters */
    const handleReset = () => {
        setSearchMobile("");
        setFilterMonth("");
        setAppliedMobile("");
        setAppliedMonth("");
        setPage(1);
    };

    /** Export to Excel */
    const exportToExcel = () => {
        const sheet = XLSX.utils.json_to_sheet(filteredData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, sheet, "Tours");
        XLSX.writeFile(wb, "TourBookings.xlsx");
    };

    /** Export to PDF */
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Tour Bookings Report", 14, 16);
        doc.autoTable({
            startY: 22,
            head: [["ID", "Name", "Mobile", "Package", "Guests", "Booking Date"]],
            body: filteredData.map((b) => [
                b.BookingID,
                b.Name,
                b.MobileNo,
                b.PackageName,
                b.Guests,
                moment(b.BookingDate).format("DD-MM-YYYY"),
            ]),
        });
        doc.save("TourBookings.pdf");
    };

    if (loading) return <p>Loading...</p>;

    const monthYearList = [
        ...new Set(
            bookings.map((b) => moment(b.BookingDate).format("YYYY-MM"))
        ),
    ].sort().reverse();

    const formatLabel = (my) => moment(my, "YYYY-MM").format("MMMM YYYY");

    return (
        <div className="container mt-4">
            <h4 className="mb-3 fw-bold">Tour Booking List</h4>

            {/*  Filters */}
            <div className="card p-3 mb-3 shadow-sm">
                <div className="row">
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by Mobile"
                            value={searchMobile}
                            onChange={(e) => setSearchMobile(e.target.value)}
                        />
                    </div>

                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                        >
                            <option value="">Filter by Month</option>
                            {monthYearList.map((my) => (
                                <option key={my} value={my}>
                                    {formatLabel(my)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-2 text-start">
                        <button className="btn btn-primary me-2" onClick={handleApplyFilter}>
                            Show
                        </button>
                        <button className="btn btn-secondary me-2" onClick={handleReset}>
                            Reset
                        </button>
                    </div>

                    <div className="col-md-4 text-end">

                        <button className="btn btn-success me-2" onClick={exportToExcel}>
                            Export to Excel
                        </button>
                        <button className="btn btn-danger" onClick={exportToPDF}>
                            Export to PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <table className="table table-bordered table-striped shadow-sm">
                <thead>
                    <tr>
                        <th>SL No.</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Package</th>
                        <th>Guests</th>
                        <th>Booking Date</th>
                        <th>Enquiry Date</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.length === 0 ? (
                        <tr>
                            <td colSpan="8" className="text-center">
                                No records found
                            </td>
                        </tr>
                    ) : (
                        paginatedData.map((item, index) => (
                            <tr key={item.BookingID}>
                                <td>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                <td>{item.Name}</td>
                                <td>{item.Email}</td>
                                <td>{item.MobileNo}</td>
                                <td>{item.PackageName}</td>
                                <td>{item.Guests}</td>
                                <td>{moment(item.BookingDate).format("DD-MM-YYYY")}</td>
                                <td>{moment(item.CreatedAt).format("DD-MM-YYYY HH:mm")}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                    <ul className="pagination">
                        <li className={`page-item ${page === 1 && "disabled"}`}>
                            <button className="page-link" onClick={() => setPage(page - 1)}>
                                Prev
                            </button>
                        </li>

                        {[...Array(totalPages)].map((_, i) => (
                            <li
                                key={i}
                                className={`page-item ${page === i + 1 && "active"}`}
                            >
                                <button className="page-link" onClick={() => setPage(i + 1)}>
                                    {i + 1}
                                </button>
                            </li>
                        ))}

                        <li className={`page-item ${page === totalPages && "disabled"}`}>
                            <button className="page-link" onClick={() => setPage(page + 1)}>
                                Next
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TourBookingList;
